import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react';
const Ctx = createContext(null);
export function OsAuthProvider({ auth, children }) {
    return _jsx(Ctx.Provider, { value: auth, children: children });
}
export function useOsAuth() {
    const auth = useContext(Ctx);
    if (!auth)
        throw new Error('useOsAuth must be used inside <OsAuthProvider>');
    return auth;
}
/** Re-renders when tokens change; returns the current authenticated state and unverified claims. */
export function useOsSession() {
    const auth = useOsAuth();
    const token = useSyncExternalStore(auth.subscribe, auth.getAccessToken, auth.getAccessToken);
    return { authenticated: auth.isAuthenticated(), claims: auth.getClaims(), roles: auth.getRoles(), token, auth };
}
/** Redirects to the OS login when there is no session; renders children otherwise. */
export function AuthGuard({ children, fallback }) {
    const { authenticated, auth } = useOsSession();
    // `isLoggingOut()` keeps a sign-out from being turned into a sign-in: clearing the tokens lands here as
    // "not authenticated", and starting a login would race the navigation to the OS `/logout`.
    useEffect(() => { if (!authenticated && !auth.isLoggingOut())
        void auth.beginLogin(); }, [authenticated, auth]);
    if (!authenticated)
        return _jsx(_Fragment, { children: fallback ?? _jsx(Centered, { children: auth.isLoggingOut() ? 'Signing you out…' : 'Redirecting to sign in…' }) });
    return _jsx(_Fragment, { children: children });
}
/** Route target for the redirect URI. `onComplete(path)` should navigate (e.g. react-router `navigate(path, { replace: true })`). */
export function AuthCallback({ onComplete }) {
    const auth = useOsAuth();
    const [error, setError] = useState(null);
    useEffect(() => {
        auth.completeLogin(new URLSearchParams(window.location.search)).then(onComplete).catch((e) => setError(e instanceof Error ? e.message : String(e)));
    }, [auth, onComplete]);
    return _jsx(Centered, { tone: error ? 'danger' : 'muted', children: error ?? 'Signing you in…' });
}
function Centered({ children, tone = 'muted' }) {
    return _jsx("div", { style: { display: 'grid', placeItems: 'center', height: '100vh', color: tone === 'danger' ? 'var(--danger-fg, #b42318)' : 'var(--text-3, #71717a)' }, children: children });
}
//# sourceMappingURL=react.js.map